import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, hasPermission, useHasModule } from '@/auth/auth-store';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useLocations } from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';

export type PendingCardAuthorisation = {
  attemptId: string;
  terminalId: string;
  terminalLabel: string | null;
  receiptClientUuid?: string;
  receiptId?: string | null;
  amountMinor?: string | null;
  currency?: string | null;
  state: string;
  initiatedAt?: string | null;
  rrn?: string;
  stan?: string;
  authCode?: string;
  paymentTerminalId?: string;
  snapshotAt?: string;
};

type PendingResponse = {
  items: PendingCardAuthorisation[];
  truncated: boolean;
  investigationOnly: true;
};

function amount(minor: string | null | undefined, currency: string | null | undefined): string {
  if (!minor || !/^\d+$/.test(minor)) return '—';
  const padded = minor.padStart(3, '0');
  return `${padded.slice(0, -2)}.${padded.slice(-2)} ${currency ?? ''}`.trim();
}

/**
 * SNX-1106 / SNX-1114 (SNX-PAY-04 (c), SNX-PAY-08): the card authorisations
 * the store's tills hold neither captured nor reversed, per receipt. Read
 * only: the owning till and the acquirer resolve the money (decision 60).
 */
export function SanitexPendingCardAuthorisationsPage() {
  const t = useT('admin_ui');
  const licensed = useHasModule('sanitex');
  const permitted = useAuthStore((s) => hasPermission(s.user, 'payments.read'));
  const locations = useLocations({ status: ['active'] });
  const [locationId, setLocationId] = useState('');
  const [result, setResult] = useState<PendingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!licensed || !permitted || !locationId) return;
    let cancelled = false;
    setResult(null);
    setError(null);
    void apiClient
      .get<PendingResponse>(`/sanitex/stores/${encodeURIComponent(locationId)}/payments/pending`)
      .then((r) => {
        if (!cancelled) setResult(r.data);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            t('sanitex.pendingCards.failed', 'Pending card authorisations could not be loaded'),
          );
      });
    return () => {
      cancelled = true;
    };
  }, [licensed, permitted, locationId, t]);
  if (!licensed || !permitted)
    return (
      <p>
        {t(
          'sanitex.pendingCards.noAccess',
          'Pending card authorisations are unavailable for this account.',
        )}
      </p>
    );
  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold">
        {t('sanitex.pendingCards.title', 'Pending card authorisations')}
      </h1>
      <p>
        {t(
          'sanitex.pendingCards.hint',
          'Card authorisations the tills hold neither captured nor reversed. The owning till and the acquirer resolve them; the acquirer can confirm or cancel one only after the terminal’s End of Day.',
        )}
      </p>
      <div className="max-w-sm">
        <Label htmlFor="pending-cards-store">{t('sanitex.pendingCards.store', 'Store')}</Label>
        <Select
          id="pending-cards-store"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          <option value="">{t('sanitex.pendingCards.chooseStore', 'Choose a store')}</option>
          {(locations.data?.items ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.code} — {l.name}
            </option>
          ))}
        </Select>
      </div>
      {error && <p role="alert">{error}</p>}
      {result && result.items.length === 0 && (
        <p role="status">
          {t('sanitex.pendingCards.empty', 'No pending card authorisations at this store.')}
        </p>
      )}
      {result && result.items.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th>{t('sanitex.pendingCards.col.till', 'Till')}</th>
              <th>{t('sanitex.pendingCards.col.receipt', 'Receipt')}</th>
              <th>{t('sanitex.pendingCards.col.amount', 'Amount')}</th>
              <th>{t('sanitex.pendingCards.col.time', 'Time')}</th>
              <th>{t('sanitex.pendingCards.col.state', 'State')}</th>
              <th>{t('sanitex.pendingCards.col.paymentTerminal', 'Terminal id')}</th>
              <th>RRN</th>
              <th>STAN</th>
              <th>{t('sanitex.pendingCards.col.authCode', 'Authorisation code')}</th>
            </tr>
          </thead>
          <tbody>
            {result.items.map((p) => (
              <tr key={`${p.terminalId}:${p.attemptId}`}>
                <td>{p.terminalLabel ?? p.terminalId}</td>
                <td>{p.receiptClientUuid ?? p.receiptId ?? '—'}</td>
                <td>{amount(p.amountMinor, p.currency)}</td>
                <td>{p.initiatedAt ?? '—'}</td>
                <td>{p.state}</td>
                <td>{p.paymentTerminalId ?? '—'}</td>
                <td>{p.rrn ?? '—'}</td>
                <td>{p.stan ?? '—'}</td>
                <td>{p.authCode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {result?.truncated && (
        <p role="status">
          {t('sanitex.pendingCards.truncated', 'Only the first 1000 attempts are listed.')}
        </p>
      )}
    </div>
  );
}
